import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/admin/AdminLayout";
import { adminApi } from "@/admin/adminApi";
import { toast } from "@/admin/Toast";
import { PRODUCT_CATEGORIES } from "@/lib/categories";

function slugify(v) {
  return (v || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function ProductsListPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/products");
      setItems(Array.isArray(data) ? data : (data.items || []));
    } catch (e) {
      toast("error", "Failed to load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((p) => {
    if (catFilter && p.category !== catFilter) return false;
    if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const onDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/products/${id}`);
      toast("success", "Product deleted");
      load();
    } catch (e) {
      toast("error", "Delete failed");
    }
  };

  return (
    <AdminLayout title="Products">
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="admin-products-list">
        <input
          type="text"
          placeholder="Search by name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-10 px-3 rounded-md border border-mist-soft text-sm w-64 focus:outline-none focus:border-mist-gold"
          data-testid="products-search"
        />
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-mist-soft text-sm"
          data-testid="products-cat-filter"
        >
          <option value="">All categories</option>
          {PRODUCT_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <div className="flex-1" />
        <Link
          to="/admin/products/new"
          data-testid="new-product-btn"
          className="inline-flex items-center gap-2 px-4 h-10 rounded-md bg-mist-navy text-white text-xs uppercase tracking-wide font-semibold hover:bg-mist-navy/90"
        >
          + New product
        </Link>
      </div>
      {loading ? (
        <div className="text-mist-navy/60">Loading…</div>
      ) : (
        <div className="overflow-x-auto bg-white border border-mist-soft rounded-md">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b border-mist-soft text-xs uppercase tracking-wide text-mist-navy/60 bg-mist-off/40">
                <th className="py-2 px-3">Image</th>
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">Slug</th>
                <th className="py-2 px-3">Category</th>
                <th className="py-2 px-3">Visible</th>
                <th className="py-2 px-3">Order</th>
                <th className="py-2 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-mist-soft/60 hover:bg-mist-off/40" data-testid={`row-${p.slug}`}>
                  <td className="py-2 px-3">
                    {p.image ? (
                      <img src={p.image} alt="" className="h-10 w-14 object-contain bg-mist-off rounded" />
                    ) : (
                      <div className="h-10 w-14 bg-mist-off rounded text-[10px] text-mist-navy/50 grid place-items-center">no img</div>
                    )}
                  </td>
                  <td className="py-2 px-3 font-medium text-mist-navy">{p.name}</td>
                  <td className="py-2 px-3 text-mist-navy/70 font-mono text-xs">{p.slug}</td>
                  <td className="py-2 px-3 text-mist-navy/80">{p.category}</td>
                  <td className="py-2 px-3">{p.visible ? "Yes" : "No"}</td>
                  <td className="py-2 px-3">{p.order}</td>
                  <td className="py-2 px-3 text-right">
                    <Link to={`/admin/products/${p.id}`} className="text-mist-navy hover:text-mist-gold mr-3">Edit</Link>
                    <button type="button" onClick={() => onDelete(p.id, p.name)} className="text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-mist-navy/60">No products match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

const EMPTY = {
  name: "", slug: "", category: "general-labware", image: "",
  description: "", inquiry_subject: "", visible: true, order: 0,
  seo: { title: "", description: "" },
};

export function ProductEditPage({ mode }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (mode !== "edit") return;
    adminApi.get(`/products/${id}`)
      .then(({ data }) => setState({ ...EMPTY, ...data, seo: { ...EMPTY.seo, ...(data.seo || {}) } }))
      .catch(() => toast("error", "Failed to load product"))
      .finally(() => setLoading(false));
  }, [id, mode]);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));
  const setSeo = (k, v) => setState((s) => ({ ...s, seo: { ...s.seo, [k]: v } }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image", data.url);
      toast("success", "Image uploaded");
    } catch (err) {
      toast("error", err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!state.name.trim() || !state.slug.trim()) {
      toast("error", "Name and slug are required");
      return;
    }
    setSaving(true);
    try {
      if (mode === "edit") {
        await adminApi.patch(`/products/${id}`, state);
        toast("success", "Saved");
      } else {
        const { data } = await adminApi.post(`/products`, state);
        toast("success", "Created");
        nav(`/admin/products/${data.id}`, { replace: true });
      }
    } catch (err) {
      toast("error", err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout title="Product"><div>Loading…</div></AdminLayout>;

  return (
    <AdminLayout title={mode === "edit" ? `Edit: ${state.name}` : "New product"}>
      <form onSubmit={onSubmit} className="max-w-3xl space-y-4 bg-white border border-mist-soft rounded-md p-6" data-testid="product-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="flex flex-col text-sm">
            <span className="text-mist-navy/70 mb-1">Name*</span>
            <input
              type="text"
              value={state.name}
              onChange={(e) => {
                const v = e.target.value;
                set("name", v);
                if (mode !== "edit" && !state.slug) set("slug", slugify(v));
              }}
              className="h-10 px-3 border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold"
              required
              data-testid="input-name"
            />
          </label>
          <label className="flex flex-col text-sm">
            <span className="text-mist-navy/70 mb-1">Slug*</span>
            <input
              type="text"
              value={state.slug}
              onChange={(e) => set("slug", slugify(e.target.value))}
              className="h-10 px-3 border border-mist-soft rounded-md font-mono focus:outline-none focus:border-mist-gold"
              required
              data-testid="input-slug"
            />
          </label>
        </div>
        <label className="flex flex-col text-sm">
          <span className="text-mist-navy/70 mb-1">Category</span>
          <select
            value={state.category}
            onChange={(e) => set("category", e.target.value)}
            className="h-10 px-3 border border-mist-soft rounded-md bg-white"
            data-testid="input-category"
          >
            {PRODUCT_CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        </label>

        <div className="flex flex-col text-sm">
          <span className="text-mist-navy/70 mb-1">Image</span>
          <div className="flex items-start gap-4">
            <div className="w-32 h-24 bg-mist-off border border-mist-soft rounded flex items-center justify-center overflow-hidden">
              {state.image
                ? <img src={state.image} alt="" className="max-w-full max-h-full object-contain" data-testid="image-preview" />
                : <span className="text-xs text-mist-navy/50">No image</span>}
            </div>
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center gap-2 px-4 h-9 rounded-md border border-mist-soft cursor-pointer hover:border-mist-gold text-sm">
                {uploading ? "Uploading…" : "Upload image"}
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="upload-image" />
              </label>
              {state.image && (
                <button type="button" onClick={() => set("image", "")} className="text-xs text-red-600 hover:underline text-left">Clear image</button>
              )}
              <input
                type="text"
                value={state.image}
                onChange={(e) => set("image", e.target.value)}
                placeholder="/uploads/… (or upload above)"
                className="h-9 px-3 border border-mist-soft rounded-md text-xs font-mono w-96 max-w-full"
              />
            </div>
          </div>
        </div>

        <label className="flex flex-col text-sm">
          <span className="text-mist-navy/70 mb-1">Description</span>
          <textarea
            value={state.description}
            onChange={(e) => set("description", e.target.value)}
            rows={5}
            className="px-3 py-2 border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold"
            data-testid="input-description"
          />
        </label>

        <label className="flex flex-col text-sm">
          <span className="text-mist-navy/70 mb-1">Inquiry subject override (optional)</span>
          <input
            type="text"
            value={state.inquiry_subject}
            onChange={(e) => set("inquiry_subject", e.target.value)}
            placeholder={`Inquiry about ${state.name || "…"}`}
            className="h-10 px-3 border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold"
          />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!state.visible} onChange={(e) => set("visible", e.target.checked)} data-testid="input-visible" />
            <span>Visible on public catalog</span>
          </label>
          <label className="flex flex-col text-sm">
            <span className="text-mist-navy/70 mb-1">Order</span>
            <input
              type="number"
              value={state.order}
              onChange={(e) => set("order", parseInt(e.target.value, 10) || 0)}
              className="h-10 px-3 border border-mist-soft rounded-md w-32"
              data-testid="input-order"
            />
          </label>
        </div>

        <fieldset className="border border-mist-soft rounded-md p-4">
          <legend className="text-xs uppercase tracking-wide text-mist-navy/60 px-1">SEO</legend>
          <label className="flex flex-col text-sm mb-3">
            <span className="text-mist-navy/70 mb-1">SEO title</span>
            <input
              type="text"
              value={state.seo?.title || ""}
              onChange={(e) => setSeo("title", e.target.value)}
              className="h-10 px-3 border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold"
            />
          </label>
          <label className="flex flex-col text-sm">
            <span className="text-mist-navy/70 mb-1">SEO description</span>
            <textarea
              value={state.seo?.description || ""}
              onChange={(e) => setSeo("description", e.target.value)}
              rows={3}
              className="px-3 py-2 border border-mist-soft rounded-md focus:outline-none focus:border-mist-gold"
            />
          </label>
        </fieldset>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="save-product"
            className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-mist-gold text-mist-navy text-xs uppercase tracking-wider font-semibold hover:bg-[#b8994f] transition-all disabled:opacity-60"
          >
            {saving ? "Saving…" : (mode === "edit" ? "Save changes" : "Create product")}
          </button>
          <Link to="/admin/products" className="text-sm text-mist-navy/70 hover:text-mist-navy">Cancel</Link>
        </div>
      </form>
    </AdminLayout>
  );
}
